<template>
    <div class="jfc-wrapper">
        <Transition name="modal">
            <GenerateChecklistModal
                v-if="isGenerateChecklistModalOpen"
                @close="closeGenerateChecklistModal"
                @edit-portfolio="handleEditPortfolio"
                @duplicate-portfolio="handleDuplicatePortfolio"
            />
        </Transition>

        <Transition name="modal">
            <AILoader
                :key="loadState"
                v-if="loadState || generationError"
                :load-steps="loadState ? loadSteps[loadState] : []"
                :visible-steps-num="4"
                :error="generationError"
                :close="closeGenerator"
                :retry="retryGeneration"
                :stop="stopGeneration"
                :disclaimer="'AI can make mistakes. Check important information.'"
            />
        </Transition>

        <JFCNavigation :active-content="activeContent" @changeSidebar="handleSidebarChange" />
        <div v-if="activeContent !== 'previousResults'" class="jfc-page-content">
            <slot></slot>
        </div>
        <div class="jfc-sidebar" :class="{ 'sidebar-full': activeContent === 'previousResults' || loadState || generationError }">
            <Transition :name="!$store.state.alertMessage.position ? 'alert' : `alert-${$store.state.alertMessage.position}`">
                <AlertMessage v-if="$store.state.alertMessage.show" />
            </Transition>

            <ScrollableContainer v-if="activeContent === 'previousResults'">
                <div class="previous-results">

                    <div class="page-title">{{contentOptions[activeContent]}}</div>

                    <div class="results">
                        <div class="result-item" v-for="item in previousJFCResults" :key="item.id">
                            <div class="left">
                                <div class="job-title">{{ item.jobTitle }}</div>
                                <div class="job-company">{{ item.jobCompany }}</div>
                                <div class="portfolio-name" :title="getPortfolioName(item.portfolioShortId)">{{ getPortfolioName(item.portfolioShortId) }}</div>
                            </div>
                            <div class="center">
                                <div class="created">{{ formatDateSince(item.createdAt) }}</div>
                                <div class="score-content">
                                    <div class="score-bar">
                                        <div class="score-fill" :style="{ width: getAverageScore(item.jobFitScore) + '%' }"></div>
                                    </div>
                                    <div class="score-text">{{ getAverageScore(item.jobFitScore) + '%' }}</div>
                                </div>
                            </div>
                            <div class="right">
                                <CtaButton :loading="deletingId === item.id" :disabled="deletingId" dark-scheme size="small" type="secondary" @click="deletePreviousResult(item)">
                                    <Icon :height="24" :width="24" icon-name="delete" />
                                </CtaButton>
                                <CtaButton dark-scheme size="small" type="primary subtle" @click="selectPreviousResult(item)">View insight</CtaButton>
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollableContainer>
            <template v-else>
                <div class="jfc-sidebar-title">
                    <Icon :width="18" :height="18" icon-name="ai-sparkling-icon" />
                    {{contentOptions[activeContent]}}
                </div>
                <div class="jfc-sidebar-content">
                    <div v-if="activeContent === 'jobDescription'" class="job-description-content">
                        <div class="title">Paste a job description</div>
                        <div class="job-description-form">
                            <div class="inputs">
                                <Input dark-scheme label="Job title" v-model="jobTitle" :has-error="isJobTitleMissing">
                                    <template #alert>
                                        <span v-if="isJobTitleMissing">Job title is required</span>
                                    </template>
                                </Input>
                                <Input dark-scheme label="Company" v-model="jobCompany" :has-error="isJobCompanyMissing">
                                    <template #alert>
                                        <span v-if="isJobCompanyMissing">Company is required</span>
                                    </template>
                                </Input>
                            </div>
                            <div class="description-input-container">
                                <PromptSelector
                                    ref="promptSelectorRef"
                                    :prompt="jobDescription"
                                    :setPrompt="setJobDescription"
                                    placeholder="Paste job description"
                                    :draft="draft"
                                    :setPromptHtml="setJobDescriptionHtml"
                                    :promptHtml="jobDescriptionHtml"
                                    draftButtonText="Use example"
                                    :max-length="8000"
                                    @use-draft-clicked="handleUseDraftClicked"
                                />
                            </div>
                        </div>
                    </div>

                    <JFCResult v-if="activeContent === 'jfcResult'" :jobFitResult="jobFitResult" />

                </div>
            </template>
            <div v-if="activeContent !== 'previousResults'" class="jfc-sidebar-actions">
                <template v-if="activeContent === 'jobDescription'">
                    <CtaButton :loading="!!loadState" dark-scheme type="primary" :disabled="isPrimaryDisabled" @click="handleSubmit">
                        <Icon icon-name="ai-sparkling-icon" />
                        Analyze
                    </CtaButton>
                </template>
                <template v-if="activeContent === 'jfcResult'">
                    <CtaButton dark-scheme type="primary" @click="openGenerateChecklistModal">
                        Generate portfolio checklist
                    </CtaButton>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import JFCNavigation from "./components/JFCNavigation.vue";
import Icon from "../../common/Icon.vue";
import Input from "../../common/components/Input.vue";
import TextArea from "../../common/components/TextArea.vue";
import SimpleAiButton from "../../common/components/SimpleAiButton.vue";
import PromptSelector from "../../portfolioEditor/modals/caseStudyGenerator/components/PromptSelector.vue";
import CtaButton from "@/scripts/common/components/CtaButton.vue";
import GenerativeAiAPI from "../../api/generativeAi/GenerativeAiAPI.js";
import {AiFeatures, PollJobStatus} from "../../common/enums.js";
import {mapGetters} from "vuex";
import JFCResult from "./components/JFCResult.vue";
import {timeAgo} from "../../common/DateTimeHelper.js";
import ScrollableContainer from "../../common/sideBars/components/ScrollableContainer.vue";
import PortfoliosAPI from "../../api/portfolios/PortfoliosAPI.js";
import AILoader from "../../portfolioEditor/modals/caseStudyGenerator/components/AILoader.vue";
import portfoliosAPI from "../../api/portfolios/PortfoliosAPI.js";
import AlertMessage from "../../common/AlertMessage.vue";
import MixpanelService from "../../analytics/mixpanel.js";
import JobFitCheckAPI from "../../api/job-fit-checks/JobFitCheckAPI.js";
import {pollJob} from "@/scripts/common/utilities/pollJob.js";
import GenerateChecklistModal from "./modals/GenerateChecklistModal.vue";

export default {
    name: "JFCLayout",
    components: {
        GenerateChecklistModal,
        AlertMessage,
        AILoader,
        ScrollableContainer,
        JFCResult,
        CtaButton,
        PromptSelector,
        SimpleAiButton,
        TextArea,
        JFCNavigation,
        Icon,
        Input
    },
    data() {
        return {
            contentOptions: {
                jobDescription:  "Check your portfolio’s job fit",
                jfcResult: "Result & suggestions",
                previousResults: "Previous matches",
            },
            activeContent: "jobDescription", // jfcResult, jobDescription, previousResults

            jobTitle: "",
            jobCompany: "",
            jobDescription: "",
            jobDescriptionHtml:'',

            generationError: false,
            cancelGeneration: false,

            isJobTitleMissing: false,
            isJobCompanyMissing: false,

            jobFitResult: null,
            isGenerateChecklistModalOpen: false,

            loadState: null, // null, "analyzing", "returnToEditor", "duplicating"
            loadSteps: {
                analyzing: [
                    { id: 1, text: "Loading your portfolio..." },
                    { id: 2, text: "Scanning portfolio structure..." },
                    { id: 3, text: "Extracting case studies and project details..." },
                    { id: 4, text: "Parsing job description..." },
                    { id: 5, text: "Identifying portfolio sections..." },
                    { id: 6, text: "Extracting project titles and descriptions..." },
                    { id: 7, text: "Extracting testimonials and social proof..." },
                    { id: 8, text: "Identifying portfolio call-to-actions..." },
                    { id: 9, text: "Checking for missing sections..." },
                    { id: 10, text: "Extracting key job requirements..." },
                    { id: 11, text: "Checking for relevant keywords..." },
                    { id: 12, text: "Assessing experience alignment..." }
                ],
                returnToEditor: [
                    { id: 1, text: "Extracting key gaps..." },
                    { id: 2, text: "Refining insights for your next steps..." },
                    { id: 3, text: "Bringing the insights into the editor..." },
                    { id: 4, text: "Almost there..." },
                    { id: 5, text: "Finalizing the checklist..." }
                ],
                duplicating: [
                    { id: 1, text: "Transferring images and media..." },
                    { id: 2, text: "Cloning text and custom sections..." },
                    { id: 3, text: "Copying your portfolio structure..." },
                    { id: 4, text: "Preserving layout and design..." },
                    { id: 5, text: "Setting up your duplicate..." },
                    { id: 6, text: "Finalising duplicate..." },
                    { id: 7, text: "Your portfolio copy is almost ready..." }
                ]
            },

            draft:'About UXfolio\n' +
                'At UXfolio, we empower UX and product designers by providing a platform to create stunning portfolios that stand out to hiring managers. Our team is passionate about building intuitive, design-centric tools that streamline the hiring process and showcase designers’ best work.\n' +
                '\n' +
                'We’re looking for a Product Designer to help shape the future of UXfolio. If you’re passionate about user experience, love solving design challenges, and thrive in a fast-moving SaaS environment, we’d love to hear from you!\n' +
                '\n' +
                'Responsibilities\n' +
                '\t•\tDesign end-to-end user experiences, from concept to final implementation.\n' +
                '\t•\tConduct user research and usability testing to inform design decisions.\n' +
                '\t•\tCollaborate with product managers, engineers, and other designers to ship impactful features.\n' +
                '\t•\tDevelop wireframes, prototypes, and high-fidelity UI designs.\n' +
                '\t•\tMaintain and evolve our design system for consistency and scalability.\n' +
                '\t•\tIterate based on user feedback, data insights, and business goals.\n' +
                '\t•\tEnsure accessibility and usability best practices are met in all designs.\n' +
                '\n' +
                'Required Skills\n' +
                '\t•\tStrong visual and interaction design skills.\n' +
                '\t•\tProficiency in Figma and design system methodologies.\n' +
                '\t•\tAbility to conduct user research and translate insights into design improvements.\n' +
                '\t•\tExperience designing for SaaS or B2B platforms.\n' +
                '\t•\tStrong understanding of UX principles, usability heuristics, and accessibility standards.\n' +
                '\t•\tExcellent collaboration and communication skills.\n' +
                '\n' +
                'Preferred Skills\n' +
                '\t•\tExperience with motion design and microinteractions.\n' +
                '\t•\tKnowledge of HTML/CSS (you won’t code, but understanding feasibility is key).\n' +
                '\t•\tFamiliarity with AI-driven design tools and workflows.\n' +
                '\t•\tExperience working with analytics tools like Hotjar, Mixpanel, or Amplitude.\n' +
                '\n' +
                'Experience Level\n' +
                'Mid-Level (3–5 years of experience in product/UX design).\n' +
                '\n' +
                'Industries\n' +
                'SaaS, B2B, Design Tools, Hiring & Recruitment.\n' +
                '\n' +
                'Education & Certifications\n' +
                '\t•\tDegree in Design, HCI, or a related field is a plus, but not required.\n' +
                '\t•\tRelevant certifications (e.g., Google UX Design Certificate) are a bonus.\n' +
                '\n' +
                'Software & Tools\n' +
                '\t•\tFigma (primary design tool)\n' +
                '\t•\tMiro (for collaboration and brainstorming)\n' +
                '\t•\tNotion (for documentation and design specs)\n' +
                '\t•\tJira (for project management)\n' +
                '\t•\tHotjar or Mixpanel (for user analytics)\n' +
                '\n' +
                'Keywords\n' +
                'User-Centered Design, SaaS, B2B, UX Research, Wireframes, Prototyping, Design Systems, Accessibility, User Testing, Data-Driven Design, Cross-Functional Collaboration.\n' +
                '\n' +
                'Company Values & Culture\n' +
                'At UXfolio, we believe in designing with empathy, iterating fast, and learning continuously. Our remote-first culture fosters flexibility, ownership, and a strong focus on collaboration. We encourage innovation and provide opportunities for professional growth.\n' +
                '\n' +
                'Compensation & Benefits\n' +
                '\t•\tCompetitive salary (EUR 50,000–65,000/year, based on experience)\n' +
                '\t•\tFlexible working hours and remote work\n' +
                '\t•\tAnnual learning budget for courses, conferences, and books\n' +
                '\t•\tTeam retreats and offsite meetups\n' +
                '\t•\tHome office setup allowance\n' +
                '\n' +
                'Application Process\n' +
                '\t•\tSubmit your resume and portfolio via our application portal.\n' +
                '\t•\tShortlisted candidates will complete a design challenge (paid).\n' +
                '\t•\tFinal stage: A chat with our team to discuss culture fit and ways of working.',

            userPortfolios: [],
            deletingId: null
        }
    },
    async mounted() {
        const res = await portfoliosAPI.getPortfolios(this.viewData.loggedInUserId);
        this.userPortfolios = res.data;
    },
    methods: {
        getPortfolioName(portfolioShortId) {
            const portfolio = this.userPortfolios.find(portfolio => portfolio.shortId === portfolioShortId || portfolio._id.endsWith(portfolioShortId));
            return portfolio.name;
        },
        closeGenerator() {
            this.loadState = null;
            this.generationError = false;
        },
        retryGeneration() {
            this.generationError = false;
            this.loadState = 'analyzing';
            this.handleSubmit();
        },
        stopGeneration() {
            this.cancelGeneration = true;
            this.closeGenerator();
        },
        setJobDescription(data) {
            this.promptError = false;
            this.jobDescription = data;
        },
        setJobDescriptionHtml(data) {
            this.jobDescriptionHtml = data;
        },
        handleUseDraftClicked() {
            this.jobTitle = "Product Designer";
            this.jobCompany = "UXfolio";
        },
        handleSidebarChange(type) {
            if (type === "jobDescription") {
                this.jobTitle = "";
                this.jobCompany = "";
                this.jobDescription = "";
                this.jobDescriptionHtml = "";
                this.isJobTitleMissing = false;
                this.isJobCompanyMissing = false;

                // Clear PromptSelector editor via ref
                this.$nextTick(() => {
                    this.$refs.promptSelectorRef?.clearEditor?.();
                });
            }

            this.activeContent = type;
        },
        selectPreviousResult(item) {
            this.jobFitResult = item;
            this.activeContent = 'jfcResult';
            this.$store.commit("jfc/setActiveJFCResult", item);
            this.$router.replace(`/jobfitchecker/${item.portfolioShortId}`);
        },
        formatDateSince(date) {
            return timeAgo(date);
        },
        async deletePreviousResult(item) {
            try {
                this.deletingId = item.id;
                await JobFitCheckAPI.deleteJobFitCheck(item.id);
                await this.$store.dispatch("jfc/fetchPreviousJFCResults");
                this.deletingId = null;
                this.$store.commit('showAlertMessage',{type:'success', message:'Insight deleted.'});
            } catch (err) {
                console.error("Failed to delete and refresh JFC results", err);
                this.deletingId = null;
                this.$store.commit('showAlertMessage',{type:'alert', message:"Can't delete insight"});
            }
        },
        getAverageScore(jobFitScore) {
            return Math.round((jobFitScore.hardSkills + jobFitScore.experience + jobFitScore.toolsSoftware + jobFitScore.industryExperience) / 4);
        },
        async handleSubmit() {
            this.validateInput();
            if (this.hasError) return

            try {
                this.loadState = 'analyzing';
                const { _id: userId } = this.viewData.user
                const { shortId: portfolioShortId } = this.viewData.portfolio;

                const jfcData = {
                    jobTitle: this.jobTitle,
                    jobCompany: this.jobCompany,
                    jobDescription: this.jobDescription,
                    portfolioShortId: portfolioShortId,
                    ownerId: userId
                };

                const response = await GenerativeAiAPI.generateJobFitCheck(jfcData);
                console.log(response);
                const result = await this.pollResponseEndpoint(response.data.jobId);
                console.log(result);

                if (result?.status === PollJobStatus.CANCELLED) {
                    return
                }

                if (!result || result.status !== "COMPLETED" || result.error) {
                    this.generationError = true;
                    return;
                }

                const jfcResult = result.result;

                this.$store.commit("jfc/setActiveJFCResult", jfcResult);
                this.$store.commit("jfc/updateJFCResults", jfcResult);
                this.jobFitResult = jfcResult;
                this.activeContent = "jfcResult";

                this.$store.state.user.featureTokens[AiFeatures.JOB_FIT_CHECKER] -= 1;
            } catch (e) {
                this.generationError = true;
                console.error('Failed to generate with AI:', e);
            } finally {
                this.loadState = null;

                MixpanelService.track("ANALYSE_CTA_CLICKED", {
                    ...MixpanelService.getUrls(),
                    event_group: MixpanelService.MixpanelEventGroups.JOB_FIT_CHECKER,
                    source: "Job Description Input Page",
                });
            }
        },
        async pollResponseEndpoint(jobId) {
            this.cancelGeneration = false;

            try {
                const payload = await pollJob(jobId, {
                    pollInterval: 3000,
                    failureMessage: "Failed to generate AI response.",
                    timeoutMessage: "AI response timed out.",
                    resolveWithPayload: true,
                    shouldCancel: () => this.cancelGeneration,
                    cancelResult: { status: PollJobStatus.CANCELLED }
                });
                return payload;
            } catch (e) {
                console.error("Failed to generate AI response", e);
                return null;
            }
        },
        async handleEditPortfolio() {
            this.loadState = 'returnToEditor';

            await JobFitCheckAPI.linkJobFitCheckToPortfolio(
                this.viewData.portfolio.shortId,
                this.jobFitResult.id,
                this.viewData.loggedInUserId
            );

            MixpanelService.track("EDIT_PORTFOLIO_CLICKED", {
                ...MixpanelService.getUrls(),
                event_group: MixpanelService.MixpanelEventGroups.JOB_FIT_CHECKER,
                source: "Insights Page",
            });

            this.$router.push({
                path: `/home/portfolios/${this.viewData.portfolio.shortId}`,
                query: { skipJfc: true }
            });
        },
        async handleDuplicatePortfolio() {
            this.loadState = 'duplicating';
            try {
                const jobResponse = await PortfoliosAPI.duplicatePortfolio(this.viewData.loggedInUserId, this.$store.state.portfolio, `${this.jobFitResult.jobTitle} ${this.jobFitResult.jobCompany} Portfolio`);
                const duplicatedPortfolio = await pollJob(jobResponse.data.jobId, {
                    failureMessage: "Portfolio duplication failed.",
                    timeoutMessage: "Portfolio duplication timed out."
                });
                const newPortfolioShortId = duplicatedPortfolio.shortId;

                this.jobFitResult.portfolioShortId = newPortfolioShortId;
                await JobFitCheckAPI.updateJobFitCheck(this.jobFitResult);

                await JobFitCheckAPI.linkJobFitCheckToPortfolio(
                    newPortfolioShortId,
                    this.jobFitResult.id,
                    this.viewData.user._id
                );

                MixpanelService.track("DUPLICATE_EDIT_CLICKED", {
                    ...MixpanelService.getUrls(),
                    event_group: MixpanelService.MixpanelEventGroups.JOB_FIT_CHECKER,
                    source: "Insights Page",
                });

                this.$store.state.portfolio = duplicatedPortfolio;
                this.$router.push({
                    path: `/home/portfolios/${newPortfolioShortId}`,
                    query: { skipJfc: true }
                });
            } catch (error) {
                console.error("Failed to duplicate portfolio", error);
                this.generationError = true;
            } finally {
                this.loadState = null;
            }
        },
        openGenerateChecklistModal() {
            this.isGenerateChecklistModalOpen = true;
        },
        closeGenerateChecklistModal() {
            this.isGenerateChecklistModalOpen = false;
        },
        validateInput() {
            this.isJobTitleMissing = this.jobTitle.trim().length === 0;
            this.isJobCompanyMissing = this.jobCompany.trim().length === 0;
        }
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        ...mapGetters("jfc", ["previousJFCResults"]),
        isPrimaryDisabled() {
            return !this.jobDescription.length || !this.jobTitle.length || !this.jobCompany.length;
        },
        hasError() {
            return this.isJobTitleMissing || this.isJobCompanyMissing;
        }
    }
}
</script>

<style scoped lang="scss">
.jfc-wrapper {
    .loader-container {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 999;
        width: 100%;
        height: 100%;
        background: var(--asphalt100);
        :deep(.loading-state) {
            width: 450px;
        }
    }

    .jfc-page-content {
        min-width: 1140px;
        border-color: transparent;
        border-style: solid;
        border-width: 0 430px 0 0;
    }

    .jfc-sidebar {
        position: fixed;
        right: 0;
        top: 0;
        height: 100vh;
        width: 430px;
        color: var(--asphalt10);
        background: var(--asphalt95);
        box-shadow:
            -15px 15px 15px -10px rgba(71, 84, 97, 0.16),
            0px 0px 20px 0px rgba(0, 0, 0, 0.05);
        margin: 60px 0 0 0;
        z-index: 100;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .jfc-sidebar-title {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 20px 24px;
            font: var(--editor-ui-b1-semibold);
        }

        .jfc-sidebar-content {
            display: flex;
            flex-direction: column;
            gap: 24px;
            flex: 1 1 auto;
            overflow-y: auto;
            max-height: calc(100vh - 220px);
            scrollbar-color: #888 transparent;

            .job-description-content {
                padding: 12px 24px 0;
                display: flex;
                flex-direction: column;
                gap: 24px;

                .title {
                    font: var(--editor-ui-b1-regular);
                }

                .job-description-form {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;

                    .inputs {
                        display: flex;
                        gap: 24px;
                    }
                }
            }
        }

        .jfc-sidebar-actions {
            display: flex;
            gap: 24px;
            padding: 24px;
            position: fixed;
            bottom: 0;
            width: inherit;
            flex-shrink: 0;
            background: var(--asphalt95);

            :deep(button.ufo-cta) {
                width: 100%;
            }
        }

        &.sidebar-full {
            width: 100%;
            overflow-y: auto;
            &::-webkit-scrollbar {
                display: none;
            }
            scrollbar-width: none;
            -ms-overflow-style: none;

            .previous-results {
                padding: 172px 0;
                width: 776px;
                margin: 0 auto;

                .page-title {
                    font: var(--editor-ui-h3);
                    color: var(--asphalt10);
                    margin-bottom: 24px;
                }
                .results {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                    .result-item {
                        display: flex;
                        justify-content: space-between;
                        gap: 24px;
                        align-items: center;
                        padding: 32px;
                        border: 1px solid var(--asphalt80);
                        border-radius: 8px;
                        font: var(--editor-ui-b1-regular);
                        color: var(--asphalt40);
                        .left,
                        .center,
                        .right {
                            display: flex;
                            flex: 1;
                        }
                        .left,
                        .center {
                            flex-direction: column;
                            gap: 4px;
                        }
                        .left {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        .right {
                            gap: 12px;
                            justify-content: flex-end;

                            > :deep(.ufo-cta.small) {
                                padding: 0;
                                width: 36px;
                                &:nth-child(2) {
                                    padding: 0 16px;
                                    width: auto;
                                    height: 38px;
                                }
                            }
                        }
                        .job-title {
                            color: var(--asphalt10);
                        }
                        .job-title,
                        .job-company,
                        .portfolio-name {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        .job-company,
                        .portfolio-name {
                            font: var(--editor-ui-b3-regular);
                        }
                        .score-content {
                            display: flex;
                            gap: 12px;
                            align-items: center;
                            .score-text {
                                color: var(--asphalt15);
                            }
                            .score-bar {
                                width: 100%;
                                height: 12px;
                                border: 1px solid var(--asphalt80);
                                border-radius: 6px;
                                .score-fill {
                                    height: 100%;
                                    background: var(--volt);
                                    border-radius: 4px;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}



:deep(input) {
    width: 100%;
}

:deep(.generator-prompt-input-container .input-text-box) {
    min-height: 250px;
    max-height: 400px;
}
</style>